import { useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../lib/supabase';

// Shown to under-13 users while parent_consent_status = 'pending'.
// Refreshes profile on window focus so as soon as the parent clicks the email
// link (which flips consent to 'granted' server-side), the child sees the app.
export function ParentalConsentPendingScreen() {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleResend() {
    if (!user || !profile?.parent_email) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const r = await fetch('/api/consent/send-parental-request', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ parentEmail: profile.parent_email, resend: true }),
      });
      if (!r.ok) {
        const detail = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error || `Could not resend (${r.status}).`);
      }
      setMessage('Sent again. Check the inbox and spam folder.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleCheckNow() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await refreshProfile();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Waiting for your parent">
      <div className="px-5 pt-6 pb-8 max-w-md mx-auto text-center">
        <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-accentSoft flex items-center justify-center">
          <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-accent">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">We&apos;ve emailed your parent</h1>
        <p className="mt-3 text-sm text-ink/70 leading-relaxed">
          We sent a link to <strong className="text-ink">{profile?.parent_email ?? 'your parent'}</strong>.
          When they click it, we&apos;ll unlock My Dyslexia Tools for you. You don&apos;t need to do anything
          right now.
        </p>

        <div className="mt-6 rounded-2xl bg-white border border-ink/10 p-5 text-left">
          <p className="text-sm font-semibold text-ink mb-2">If they haven&apos;t got it yet</p>
          <ul className="text-sm text-ink/75 space-y-1.5 list-disc pl-5">
            <li>Ask them to check their spam / junk folder.</li>
            <li>The link expires after 7 days - use &quot;Send it again&quot; below if it&apos;s been longer.</li>
            <li>Make sure the email address is right - if not, sign out and start again.</li>
          </ul>
        </div>

        {message && (
          <div role="status" className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm">
            {message}
          </div>
        )}
        {error && (
          <div role="alert" className="mt-4 rounded-xl bg-warm border border-amber-300 text-ink px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={handleCheckNow}
            disabled={busy}
            className="inline-flex items-center justify-center bg-accent text-white px-6 py-3 rounded-full font-medium hover:brightness-105 transition disabled:opacity-60"
          >
            {busy ? 'Checking...' : 'I&apos;m ready - check now'}
          </button>
          <button
            onClick={handleResend}
            disabled={busy}
            className="inline-flex items-center justify-center bg-white text-accent border border-accent/30 px-6 py-3 rounded-full font-medium hover:bg-accent hover:text-white transition disabled:opacity-60"
          >
            Send it again
          </button>
          <button
            onClick={() => void signOut()}
            className="text-xs text-ink/50 underline mt-3"
          >
            Sign out
          </button>
        </div>
      </div>
    </AppShell>
  );
}
